/**
 * Начальное состояние action-кнопок из jsResult.states (ответ openForm / elementAction).
 *
 * Проблема: MAction монтируется с isVisible=true, а processStates (Form.onMounted + setTimeout)
 * и applyPendingActionStates применяют is_visible позже — кнопка «мигает» (form 2573 / accept_btn 27541).
 *
 * Решение: при создании MAction читаем is_visible / is_enabled из pageStates до первого paint.
 * Если state для action нет — остаётся defaultValue (обычно true), processStates по-прежнему
 * может изменить видимость позже (показать скрытую кнопку и т.п.).
 *
 * @see MAction.vue — resolveActionInitialFlag при инициализации isVisible / isEnabled
 * @see useFormStates.processStates — отложенное применение states после регистрации action
 */
import type { ElementState } from '@/common/composables/usePageData'
import { actionIdsEqual } from '@/common/utils/resolveActionRegistryEntry'

export function coerceStateBoolean(value: unknown): boolean | undefined {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'boolean') return value
  const text = String(value).trim().toLowerCase()
  if (text === 'true' || text === '1') return true
  if (text === 'false' || text === '0') return false
  return Boolean(value)
}

export function findActionPageState(
  states: ElementState[] | undefined,
  actionId: number,
  actionName: string,
): ElementState | undefined {
  if (!Array.isArray(states) || !Number.isFinite(actionId)) return undefined
  const name = String(actionName ?? '').trim()

  const byId = states.find(
    (state) =>
      String(state?.element_level ?? '').toUpperCase() === 'ACTION' &&
      actionIdsEqual(state.element_id, actionId),
  )
  if (byId) return byId

  if (name === '') return undefined

  return states.find((state) => {
    if (String(state?.element_level ?? '').toUpperCase() !== 'ACTION') return false
    const stateId = state.element_id
    if (stateId != null && String(stateId).trim() !== '') return false
    return String(state.element_name ?? '').trim() === name
  })
}

function coalesceElementStates(states: ElementState[]): ElementState {
  const base: ElementState = { ...states[0] }
  let coalescedVisible: boolean | undefined
  let coalescedEditable: boolean | undefined

  for (const state of states) {
    const visible = coerceStateBoolean(state.is_visible)
    if (visible === true) {
      coalescedVisible = true
    } else if (visible === false && coalescedVisible !== true) {
      coalescedVisible = false
    }

    const editable = coerceStateBoolean(state.is_editable)
    if (editable === undefined) continue
    const stateVisible = coerceStateBoolean(state.is_visible)
    if (editable === true) {
      coalescedEditable = true
    } else if (editable === false && stateVisible !== false) {
      coalescedEditable = false
    }
  }

  if (coalescedVisible !== undefined) {
    base.is_visible = coalescedVisible
  } else {
    delete base.is_visible
  }
  if (coalescedEditable !== undefined) {
    base.is_editable = coalescedEditable
  } else {
    delete base.is_editable
  }
  return base
}

/** Widget states only — PAGE/PANEL/ACTION must not coalesce into field visibility. */
export function isElementLevelState(level: unknown): boolean {
  const norm = String(level ?? '').trim().toUpperCase()
  return norm === '' || norm === 'ELEMENT'
}

export function findElementPageState(
  states: ElementState[] | undefined,
  elementId: number,
  elementName: string,
): ElementState | undefined {
  if (!Array.isArray(states)) return undefined
  const name = String(elementName ?? '').trim()
  const matches = states.filter((state) => {
    if (!isElementLevelState(state?.element_level)) return false
    return (
      actionIdsEqual(state.element_id, elementId) ||
      (name !== '' && String(state.element_name ?? '').trim() === name)
    )
  })
  if (matches.length === 0) return undefined
  if (matches.length === 1) return matches[0]
  return coalesceElementStates(matches)
}

export function resolveActionInitialFlag(
  states: ElementState[] | undefined,
  actionId: number,
  actionName: string,
  key: 'is_visible' | 'is_enabled' | 'is_editable',
  defaultValue: boolean,
): boolean {
  const state = findActionPageState(states, actionId, actionName)
  if (!state) return defaultValue
  const resolved = coerceStateBoolean(state[key])
  return resolved !== undefined ? resolved : defaultValue
}

/** Платформа часто шлёт is_editable:false вместе с is_visible:false для скрытых полей — не блокировать ввод после показа. */
export function resolvePlatformElementEditableFromState(
  state: ElementState | undefined,
  defaultEditable: boolean,
): boolean | undefined {
  if (!state) return undefined
  const resolved = coerceStateBoolean(state.is_editable)
  if (resolved === undefined) return undefined
  const visible = coerceStateBoolean(state.is_visible)
  if (resolved === false && visible === false) {
    return defaultEditable
  }
  return resolved
}

/** Начальная редактируемость поля из jsResult.states (form 2581 email / clientPd.name и др.). */
export function resolveElementInitialEditable(
  states: ElementState[] | undefined,
  elementId: number,
  elementName: string,
  defaultEditable: boolean,
): boolean {
  const state = findElementPageState(states, elementId, elementName)
  if (!state) return defaultEditable
  const resolved = resolvePlatformElementEditableFromState(state, defaultEditable)
  return resolved !== undefined ? resolved : defaultEditable
}

/** Явный is_visible из pageStates, без default — чтобы не перекрывать registry. */
export function resolveElementExplicitVisible(
  states: ElementState[] | undefined,
  elementId: number,
  elementName: string,
): boolean | undefined {
  const state = findElementPageState(states, elementId, elementName)
  if (!state) return undefined
  return coerceStateBoolean(state.is_visible)
}

/**
 * Видимость виджета: shell map → явный pageStates is_visible → registry → default.
 * Registry default true не должен прятать getPage is_visible:false.
 */
export function resolveWidgetElementVisible(args: {
  shellVisible?: boolean
  statesVisible?: boolean
  registryVisible?: boolean
  defaultVisible?: boolean
}): boolean {
  if (typeof args.shellVisible === 'boolean') return args.shellVisible
  if (typeof args.statesVisible === 'boolean') return args.statesVisible
  if (typeof args.registryVisible === 'boolean') return args.registryVisible
  return args.defaultVisible !== false
}

/** Начальная видимость поля из jsResult.states */
export function resolveElementInitialVisible(
  states: ElementState[] | undefined,
  elementId: number,
  elementName: string,
  defaultVisible = true,
): boolean {
  const explicit = resolveElementExplicitVisible(states, elementId, elementName)
  return explicit !== undefined ? explicit : defaultVisible
}
